import { getOpacity, hexToRgba, Rgba2hex } from "../../../utils/helper";
import interact from "interactjs";

export class CustomShape {
  static GetStyleFromShape = ({ iTag, setShapeSettings, reset = false }) => {
    const isBodyTransParent = iTag.style.color === "rgba(0, 0, 0, 0)";
    const isBorderTransParent =
      iTag.style.getPropertyValue("-webkit-text-stroke-color") ===
      "rgba(0, 0, 0, 0)";
    const newShapeSettings = {
      addSelectedShapeFlag: null,
      addShapeToMapFlag: false,
      selectedShape: {
        id: iTag.id,
        style: {
          isBackgroundFilled: !isBodyTransParent,
          isBorderFilled: !isBorderTransParent,
          backgroundColor: isBodyTransParent
            ? Rgba2hex(iTag.dataset.backgroundColor, true)
            : Rgba2hex(iTag.style.color, true),
          borderColor: isBorderTransParent
            ? Rgba2hex(iTag.dataset.borderColor, true)
            : Rgba2hex(iTag.style["-webkit-text-stroke-color"], true),
          borderWidth: parseFloat(iTag.style.borderWidth),
          opacity: String(getOpacity(iTag.dataset.backgroundColor)),
        },
        reset: reset,
      },
    };
    setShapeSettings(newShapeSettings);
  };

  static AddShapeEventListener = ({
    map,
    outerDiv,
    iTag,
    marker,
    lastClickButton,
    setShapeSettings,
    resetAllOnceChanged,
    disableUserActions,
  }) => {
    // Event listener to show the button on right-click
    outerDiv.addEventListener("contextmenu", (event) => {
      if (disableUserActions()) {
        return;
      }
      resetAllOnceChanged();
      lastClickButton.current = 0;
      this.GetStyleFromShape({ iTag, setShapeSettings, reset: true });
      event.preventDefault(); // Prevent the default context menu from showing
    });

    // Use interactjs to make the shape resizable
    interact(iTag).resizable({
      edges: { left: false, right: true, bottom: true, top: false }, // Allow resizing from all directions

      listeners: {
        move(event) {
          if (disableUserActions()) {
            return;
          }
          let target = event.target;
          // Calculate the new width and height
          const newWidth = event.rect.width;
          const newHeight = event.rect.height;
          // Calculate the new font size based on the average of width and height
          let newFontSize = (newWidth + newHeight) / 2;
          newFontSize = Math.max(25, newFontSize);
          target.style.fontSize = newFontSize + "px";
        },
        end() {
          map.dragPan.enable();
          marker.setDraggable(disableUserActions() ? false : true);
        },
      },
      modifiers: [
        interact.modifiers.restrictEdges({
          outer: "parent",
          endOnly: true,
          enabled: false,
        }),
      ],
      inertia: true,
    });

    iTag.addEventListener("mouseenter", function () {
      if (!disableUserActions()) iTag.classList.add("dragging");
    });

    iTag.addEventListener("mouseleave", function () {
      if (!disableUserActions()) iTag.classList.remove("dragging");
    });

    // Check for mouse event for shape
    iTag.addEventListener("mousedown", function (event) {
      const shapeRect = iTag.getBoundingClientRect();
      const offsetX = event.clientX - shapeRect.right;
      const offsetY = event.clientY - shapeRect.bottom;
      const threshold = 15;
      const isRightEdge = Math.abs(offsetX) <= threshold;
      const isBottomEdge = Math.abs(offsetY) <= threshold;
      if (isRightEdge || isBottomEdge) {
        marker.setDraggable(false);
        map.dragPan.disable();
      }
      if (disableUserActions()) {
        return;
      } else {
        iTag.classList.add("dragging");
        document.addEventListener("mousemove", onDrag);
        document.addEventListener("mouseup", stopDrag);
      }
    });

    function onDrag(event) {
      iTag.classList.add("dragging");
    }

    function stopDrag() {
      iTag.classList.remove("dragging");
      document.removeEventListener("mousemove", onDrag);
      document.removeEventListener("mouseup", stopDrag);
    }

    iTag.addEventListener("mouseleave", function (event) {
      if (!disableUserActions()) marker.setDraggable(true);
      map.dragPan.enable();
    });
  };

  static AddShapeToMap = ({
    coordinates,
    shapeType,
    mapboxgl,
    map,
    lastClickButton,
    shapeStyleSettings,
    resetAllOnceChanged,
    // updateAutoSave,
    setShapeSettings,
    setMapToolBarOptionFlags,
    disableUserActions,
    markerRef,
  }) => {
    // Create the outer div element
    const outerDiv = document.createElement("div");
    const inputMarkerId =
      "shape-" + String(coordinates.lng) + "-" + String(coordinates.lat);
    // Create the shape element
    const iTag = document.createElement("i");
    iTag.id = inputMarkerId;
    iTag.dataset.type = "shape";
    iTag.style.resize = "both";
    iTag.style.fontSize = "60px";
    iTag.style.color = "rgba(0, 0, 0, 0)";
    iTag.style.opacity = "100%";
    iTag.style.borderWidth = String(shapeStyleSettings.borderWidth) + "px";
    iTag.style.setProperty("-webkit-text-stroke", "3px");
    iTag.style.setProperty(
      "-webkit-text-stroke-color",
      shapeStyleSettings.borderColor
    );
    iTag.style.setProperty(
      "-webkit-text-stroke-width",
      shapeStyleSettings.borderWidth
    );
    iTag.className = shapeType;
    iTag.dataset.isBackgroundFill = "false";
    iTag.dataset.isBorderFill = "true";
    iTag.dataset.backgroundColor = hexToRgba(
      shapeStyleSettings.backgroundColor,
      100
    );
    iTag.dataset.borderColor = shapeStyleSettings.borderColor;
    iTag.dataset.borderWidth = shapeStyleSettings.borderWidth;
    outerDiv.appendChild(iTag);
    // Create a draggable marker
    const marker = new mapboxgl.Marker(outerDiv, {
      draggable: true,
      className: "marker-remove-default-size",
      anchor: "top-left",
    })
      .setLngLat([coordinates.lng, coordinates.lat])
      .addTo(map);

    markerRef.current.push({
      id: inputMarkerId,
      marker: marker,
      type: "shape",
    });
    // updateAutoSave();
    this.GetStyleFromShape({ iTag, setShapeSettings });
    this.AddShapeEventListener({
      map,
      outerDiv,
      iTag,
      marker,
      lastClickButton,
      setShapeSettings,
      resetAllOnceChanged,
      disableUserActions,
    });
    map.getCanvas().style.cursor = "default";
    setMapToolBarOptionFlags((prev) => ({
      ...prev,
      shape: false,
    }));
  };

  static RecoverShapeToMap = ({
    coordinates,
    shapeTag,
    mapboxgl,
    map,
    lastClickButton,
    resetAllOnceChanged,
    setShapeSettings,
    disableUserActions,
    markerRef,
  }) => {
    // Create the outer div element
    const outerDiv = document.createElement("div");
    const inputMarkerId = shapeTag.id;
    outerDiv.appendChild(shapeTag);

    // Create a draggable marker
    const marker = new mapboxgl.Marker(outerDiv, {
      draggable: true,
      className: "marker-remove-default-size",
      anchor: "top-left",
    })
      .setLngLat([coordinates.lng, coordinates.lat])
      .addTo(map);

    markerRef.current.push({
      id: inputMarkerId,
      marker: marker,
      type: "shape",
    });

    this.AddShapeEventListener({
      map,
      outerDiv,
      iTag: shapeTag,
      marker,
      lastClickButton,
      setShapeSettings,
      resetAllOnceChanged,
      disableUserActions,
    });
    map.getCanvas().style.cursor = "default";
  };
}
